import React from 'react';
import { Card, Typography, Button, Row, Col, Statistic, Space, Table, Modal, Form, Input, InputNumber, Select, Tag, Switch } from 'antd';
import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { superadminApi } from '../../services/superadminApi';

const { Title, Text } = Typography;

const SuperadminDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [plans, setPlans] = useState([]);
  const [discounts, setDiscounts] = useState([]);

  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);

  const [planForm] = Form.useForm();
  const [discountForm] = Form.useForm();

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [an, ps, ds] = await Promise.all([
          superadminApi.getAnalytics().catch(() => null),
          superadminApi.listPlans().catch(() => []),
          superadminApi.listDiscounts().catch(() => []),
        ]);
        if (!mounted) return;
        setAnalytics(an);
        setPlans(ps || []);
        setDiscounts(ds || []);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const planColumns = useMemo(() => ([
    { title: 'Name', dataIndex: 'name' },
    { title: 'Code', dataIndex: 'code' },
    { title: 'Price', dataIndex: 'price', render: (v) => `₹${v}` },
    { title: 'Interval', dataIndex: 'interval', render: (v) => <Tag color={v === 'yearly' ? 'purple' : 'blue'}>{v}</Tag> },
    { title: 'Active', dataIndex: 'active', render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? 'Yes' : 'No'}</Tag> },
    { title: 'Features', dataIndex: 'features', render: (arr = []) => (arr || []).join(', ') },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => onEditPlan(record)}>Edit</Button>
          <Button danger size="small" onClick={() => onDeletePlan(record._id)}>Delete</Button>
        </Space>
      ),
    },
  ]), []);

  const discountColumns = useMemo(() => ([
    { title: 'Name', dataIndex: 'name' },
    { title: 'Code', dataIndex: 'code' },
    { title: 'Type', dataIndex: 'type', render: (v) => <Tag color={v === 'percent' ? 'gold' : 'cyan'}>{v}</Tag> },
    { title: 'Value', dataIndex: 'value' },
    { title: 'Active', dataIndex: 'active', render: (v) => <Tag color={v ? 'green' : 'red'}>{v ? 'Yes' : 'No'}</Tag> },
    { title: 'Max Redemptions', dataIndex: 'maxRedemptions', render: (v) => v ?? '-' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => onEditDiscount(record)}>Edit</Button>
          <Button danger size="small" onClick={() => onDeleteDiscount(record._id)}>Delete</Button>
        </Space>
      ),
    },
  ]), []);

  const onCreatePlan = () => {
    setEditingPlan(null);
    planForm.resetFields();
    planForm.setFieldsValue({ interval: 'monthly', active: true, features: '' });
    setPlanModalOpen(true);
  };
  const onEditPlan = (plan) => {
    setEditingPlan(plan);
    planForm.resetFields();
    planForm.setFieldsValue({
      name: plan.name,
      code: plan.code,
      price: plan.price,
      interval: plan.interval,
      active: plan.active,
      features: (plan.features || []).join(', '),
    });
    setPlanModalOpen(true);
  };
  const onDeletePlan = async (id) => {
    await superadminApi.deletePlan(id);
    const ps = await superadminApi.listPlans();
    setPlans(ps || []);
  };
  const onSubmitPlan = async () => {
    const values = await planForm.validateFields();
    const payload = {
      ...values,
      features: (values.features || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean),
    };
    if (editingPlan?._id) {
      await superadminApi.updatePlan(editingPlan._id, payload);
    } else {
      await superadminApi.createPlan(payload);
    }
    setPlanModalOpen(false);
    const ps = await superadminApi.listPlans();
    setPlans(ps || []);
  };

  const onCreateDiscount = () => {
    setEditingDiscount(null);
    discountForm.resetFields();
    discountForm.setFieldsValue({ type: 'percent', active: true });
    setDiscountModalOpen(true);
  };
  const onEditDiscount = (d) => {
    setEditingDiscount(d);
    discountForm.resetFields();
    discountForm.setFieldsValue({
      name: d.name,
      code: d.code,
      type: d.type,
      value: d.value,
      maxRedemptions: d.maxRedemptions,
      active: d.active,
    });
    setDiscountModalOpen(true);
  };
  const onDeleteDiscount = async (id) => {
    await superadminApi.deleteDiscount(id);
    const ds = await superadminApi.listDiscounts();
    setDiscounts(ds || []);
  };
  const onSubmitDiscount = async () => {
    const values = await discountForm.validateFields();
    if (editingDiscount?._id) {
      await superadminApi.updateDiscount(editingDiscount._id, values);
    } else {
      await superadminApi.createDiscount(values);
    }
    setDiscountModalOpen(false);
    const ds = await superadminApi.listDiscounts();
    setDiscounts(ds || []);
  };

  return (
    <div className="min-h-screen p-6 bg-slate-50 dark:bg-slate-900">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <Title level={2} className="!mb-1 dark:!text-slate-100">Superadmin Dashboard</Title>
          <Text className="dark:!text-slate-400">Manage companies, approvals, analytics, subscriptions, and discounts</Text>
        </div>

        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <Card loading={loading}>
              <Statistic title="Total Companies" value={analytics?.totalCompanies ?? 0} />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card loading={loading}>
              <Statistic title="Active Companies" value={analytics?.activeCompanies ?? 0} />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card loading={loading}>
              <Statistic title="Admin Users" value={analytics?.adminCount ?? 0} />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card loading={loading}>
              <Statistic title="Company Owners" value={analytics?.ownerCount ?? 0} />
            </Card>
          </Col>
        </Row>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Card
            title="Subscription Plans"
            extra={<Button type="primary" onClick={onCreatePlan}>New Plan</Button>}
          >
            <Table
              rowKey="_id"
              size="small"
              loading={loading}
              columns={planColumns}
              dataSource={plans}
              pagination={{ pageSize: 5 }}
            />
          </Card>

          <Card
            title="Discounts"
            extra={<Button type="primary" onClick={onCreateDiscount}>New Discount</Button>}
          >
            <Table
              rowKey="_id"
              size="small"
              loading={loading}
              columns={discountColumns}
              dataSource={discounts}
              pagination={{ pageSize: 5 }}
            />
          </Card>
        </div>

        <div className="mt-4">
          <Card title="Companies" extra={<Link to="/">Go to app</Link>}>
            <p className="mb-2">Approve onboarding and manage plans.</p>
            <Button type="default"><Link to="/">Back to App</Link></Button>
          </Card>
        </div>
      </div>

      {/* Plan Modal */}
      <Modal
        title={editingPlan ? 'Edit Plan' : 'New Plan'}
        open={planModalOpen}
        onCancel={() => setPlanModalOpen(false)}
        onOk={onSubmitPlan}
        okText={editingPlan ? 'Update' : 'Create'}
      >
        <Form layout="vertical" form={planForm}>
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Code" name="code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Price (₹)" name="price" rules={[{ required: true, type: 'number', min: 0 }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Interval" name="interval" rules={[{ required: true }]}>
            <Select options={[{ value: 'monthly', label: 'Monthly' }, { value: 'yearly', label: 'Yearly' }]} />
          </Form.Item>
          <Form.Item label="Active" name="active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label="Features (comma separated)" name="features">
            <Input placeholder="e.g. 5 users,100 products,Email support" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Discount Modal */}
      <Modal
        title={editingDiscount ? 'Edit Discount' : 'New Discount'}
        open={discountModalOpen}
        onCancel={() => setDiscountModalOpen(false)}
        onOk={onSubmitDiscount}
        okText={editingDiscount ? 'Update' : 'Create'}
      >
        <Form layout="vertical" form={discountForm}>
          <Form.Item label="Name" name="name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Code" name="code" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item label="Type" name="type" rules={[{ required: true }]}>
            <Select options={[{ value: 'percent', label: 'Percent' }, { value: 'amount', label: 'Amount' }]} />
          </Form.Item>
          <Form.Item label="Value" name="value" rules={[{ required: true, type: 'number', min: 0 }]}>
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="Max Redemptions" name="maxRedemptions">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item label="Active" name="active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SuperadminDashboard;
